import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Divider from '@mui/material/Divider';

import { InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum } from 'src/utils/utility';
import { TransportMedium } from 'src/presentations/components/molecules/card/types';

import StatusChip from 'src/presentations/components/organisms/dashboard/StatusChip';
import TransportMediumIcon from 'src/presentations/components/molecules/card/TransportMediumIcon';
import IconArrow from 'src/assets/images/icons/arrow-left_ico.svg';

import { useSelector } from 'react-redux';
import { useTourContext } from 'src/presentations/guided-tours';
import { ActionDetailShipmentOptions } from './ShipmentOptions';

interface Props {
  invoiceSummary: InvoiceSummaryDto;
}

export const ActionDetailTopInfo = ({ invoiceSummary }: Props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tour = useTourContext().appTour.registerTour('actionDetails');

  const goBack = useCallback(() => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.delete('hideBack');

    if (invoiceSummary?.serviceProvider) {
      currentParams.set('provider', invoiceSummary.serviceProvider);
    }

    if (invoiceSummary?.modality) {
      currentParams.set('modality', invoiceSummary.modality);
    }

    // Preserve tableFilters if it exists in the current URL
    const tableFilters = searchParams.get('tableFilters');
    if (tableFilters) {
      currentParams.set('tableFilters', tableFilters);
    }

    // Preserve pagination parameters
    const page = searchParams.get('page');
    if (page) {
      currentParams.set('page', page);
    }

    const pageSize = searchParams.get('pageSize');
    if (pageSize) {
      currentParams.set('pageSize', pageSize);
    }

    const queryString = currentParams.toString();
    navigate(`/dashboard${queryString ? `?${queryString}` : ''}`);
  }, [navigate, searchParams, invoiceSummary?.serviceProvider, invoiceSummary?.modality]);

  const renderInvoiceStatusComponent = () => {
    let invoiceStatus = invoiceSummary?.status as string;
    if (!invoiceStatus && !invoiceSummary?.fileProcessing) {
      invoiceStatus = 'UNDER_REVIEW';
    }
    return <StatusChip status={invoiceStatus} />;
  };
  return (
    <div id={tour.registerStep('header')} className='flex justify-between items-center gap-8 xl:gap-16'>
      {!searchParams?.get('hideBack') && (
        <button
          onClick={goBack}
          className='w-40 h-40 rounded-full bg-neutral-10 border border-neutral-20 transition-all duration-300 hover:border-neutral-40 flex items-center justify-center'
        >
          <img src={IconArrow} alt='Back' />
        </button>
      )}
      <div className='flex items-center justify-center xl:ml-6'>
        <TransportMediumIcon medium={invoiceSummary?.modality as TransportMedium} iconColor='#9F9F9F' />
        <h6 className='text-16 xl:text-18 text-[#36454F] font-medium ml-6 xl:ml-10'>{beautifyEnum(invoiceSummary?.freightForwarder?.code)?.toUpperCase()}</h6>
      </div>
      <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />
      <h6 className='text-14 xl:text-18 text-primary font-medium ml-6 xl:ml-10'>{invoiceSummary?.invoiceNumber}</h6>
      <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />

      {renderInvoiceStatusComponent()}
      <Divider className='bg-neutral-20' orientation='vertical' variant='middle' flexItem />
      <ActionDetailShipmentOptions />
    </div>
  );
};
